import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { X } from 'lucide-react';
import { useState } from 'react';

type Props = { requester: string; channel: 'portal' | 'teams'; isPending: boolean; onCancel: () => void; onReject: (reason: string) => void };

/** The usual reasons, one tap each; the text stays editable. */
const QUICK: [string, string][] = [
  ['Not supported by the source', 'Not supported by the cited source.'],
  ['Needs compliance review', 'Needs compliance review before it goes to clients.'],
  ['Wrong list', 'Wrong list for this message.'],
  ['Test draft, not for sending', 'Test draft, not for sending.'],
];
const MIN_REASON = 5;

export function RejectDialog({ requester, channel, isPending, onCancel, onReject }: Props) {
  const [reason, setReason] = useState('');
  const where = channel === 'teams' ? 'in Teams' : 'in the portal';
  return (
    <Dialog open onClose={onCancel} fullWidth maxWidth="xs" aria-labelledby="reject-title" slotProps={{ paper: { sx: { borderRadius: 2 } } }}>
      <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1, px: 2.5, pt: 2.5 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography id="reject-title" variant="h4" component="h2">Reject this message</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>{requester} will be told why {where}. Nothing is sent to WhatsApp.</Typography>
        </Box>
        <IconButton size="small" aria-label="Close" onClick={onCancel}><X size={18} /></IconButton>
      </Stack>
      <Stack sx={{ gap: 2, px: 2.5, py: 2 }}>
        <Stack direction="row" role="group" aria-label="Common reasons" sx={{ gap: 0.75, flexWrap: 'wrap' }}>
          {QUICK.map(([label, text]) => (
            <ButtonBase key={label} aria-pressed={reason === text} onClick={() => { setReason(text); }} sx={chipSx(reason === text)}>{label}</ButtonBase>
          ))}
        </Stack>
        <TextField
          label="Reason"
          required
          multiline
          minRows={3}
          value={reason}
          onChange={(e) => { setReason(e.target.value); }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Stack>
      <Stack direction="row" sx={{ justifyContent: 'flex-end', gap: 1, px: 2.5, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
        <Button variant="outlined" onClick={onCancel} sx={{ color: 'text.primary', borderColor: 'divider' }}>Cancel</Button>
        <Button color="error" variant="contained" disabled={reason.trim().length < MIN_REASON || isPending} onClick={() => { onReject(reason.trim()); }}>
          Reject message
        </Button>
      </Stack>
    </Dialog>
  );
}

const chipSx = (isOn: boolean) => ({
  px: 1.25,
  height: 28,
  borderRadius: 5,
  border: 1,
  borderColor: isOn ? 'primary.light' : 'divider',
  bgcolor: isOn ? 'surface.accentSoft' : 'background.paper',
  color: isOn ? 'primary.dark' : 'text.primary',
  fontSize: '0.75rem',
  fontWeight: 500,
  transition: 'background-color 150ms, border-color 150ms',
  '&:hover': { borderColor: isOn ? 'primary.light' : 'surface.borderStrong' },
});
